import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface Admin {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'manager' | 'viewer' | 'kindergarten_admin';
  permissions: {
    kindergartens: string[];
    children: string[];
    parents: string[];
    menus: string[];
    cameras: string[];
    invites: string[];
    admins: string[];
  };
  isActive: boolean;
  createdAt: string;
}

interface AdminFormData {
  email: string;
  name: string;
  password: string;
  role: string;
  permissions: {
    kindergartens: string[];
    children: string[];
    parents: string[];
    menus: string[];
    cameras: string[];
    invites: string[];
    admins: string[];
  };
  isActive: boolean;
}

const Admins: React.FC = () => {
  const { hasPermission, admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [formData, setFormData] = useState<AdminFormData>({
    email: '',
    name: '',
    password: '',
    role: 'viewer',
    permissions: {
      kindergartens: [],
      children: [],
      parents: [],
      menus: [],
      cameras: [],
      invites: [],
      admins: [],
    },
    isActive: true,
  });

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin-auth/admins');
      setAdmins(response.data.data || []);
    } catch (error: any) {
      console.error('Помилка при отриманні адміністраторів:', error);
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (admin?: Admin) => {
    setError('');
    setSuccessMessage('');
    
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        email: admin.email,
        name: admin.name,
        password: '',
        role: admin.role,
        permissions: { ...admin.permissions },
        isActive: admin.isActive,
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'viewer',
        permissions: {
          kindergartens: [],
          children: [],
          parents: [],
          menus: [],
          cameras: [],
          invites: [],
          admins: [],
        },
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAdmin(null);
    setError('');
    setSuccessMessage('');
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'viewer',
      permissions: {
        kindergartens: [],
        children: [],
        parents: [],
        menus: [],
        cameras: [],
        invites: [],
        admins: [],
      },
      isActive: true,
    });
  };

  // Функція для перевірки чи може поточний адмін видалити іншого адміна
  const canDeleteAdmin = (adminToDelete: Admin) => {
    if (!currentAdmin) return false;
    
    // Не можна видалити себе
    if (adminToDelete.id === currentAdmin.id) return false;
    
    // Головний адміністратор може видаляти всіх
    if (currentAdmin.role === 'super_admin') return true;
    
    // Менеджер може видаляти тільки адміністраторів садка та переглядачів
    if (currentAdmin.role === 'manager') {
      return adminToDelete.role === 'kindergarten_admin' || adminToDelete.role === 'viewer';
    }
    
    // Інші ролі не можуть видаляти адміністраторів
    return false;
  };

  const handleSubmit = async () => {
    try {
      // Валідація
      if (!formData.email || !formData.name) {
        setError('Email та ім\'я обов\'язкові');
        return;
      }

      if (!editingAdmin && !formData.password) {
        setError('Пароль обов\'язковий для нового адміністратора');
        return;
      }

      if (editingAdmin) {
        await api.put(`/admin-auth/admins/${editingAdmin.id}`, formData);
      } else {
        await api.post('/admin-auth/admins', formData);
      }

      handleCloseDialog();
      
      // Показуємо повідомлення про успіх
      setSuccessMessage(editingAdmin ? 'Адміністратора успішно оновлено' : 'Адміністратора успішно створено');
      
      // Очищаємо повідомлення через 3 секунди
      setTimeout(() => setSuccessMessage(''), 3000);
      
      fetchAdmins();
    } catch (error: any) {
      console.error('Помилка при збереженні:', error);
      setError(error.response?.data?.message || 'Помилка збереження');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Ви впевнені, що хочете видалити цього адміністратора?')) {
      try {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        
        await api.delete(`/admin-auth/admins/${id}`);
        
        // Оптимістично оновлюємо список, видаляючи адміністратора з локального стану
        setAdmins(prevAdmins => prevAdmins.filter(admin => admin.id !== id));
        
        // Показуємо повідомлення про успіх
        setSuccessMessage('Адміністратора успішно видалено');
        
        // Очищаємо повідомлення через 3 секунди
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Також перезавантажуємо дані з сервера для синхронізації
        await fetchAdmins();
      } catch (error: any) {
        console.error('Помилка при видаленні адміністратора:', error);
        setError(error.response?.data?.message || 'Помилка видалення');
        
        // Якщо видалення не вдалося, перезавантажуємо список
        await fetchAdmins();
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePermissionChange = (resource: string, action: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [resource]: checked
          ? [...(prev.permissions[resource as keyof typeof prev.permissions] || []), action]
          : (prev.permissions[resource as keyof typeof prev.permissions] || []).filter(a => a !== action)
      }
    }));
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'manager': return 'warning';
      case 'kindergarten_admin': return 'info';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Головний адміністратор';
      case 'manager': return 'Менеджер';
      case 'kindergarten_admin': return 'Адміністратор садка';
      case 'viewer': return 'Переглядач';
      default: return role;
    }
  };

  const predefinedRoles = {
    super_admin: {
      kindergartens: ['create', 'read', 'update', 'delete'],
      children: ['create', 'read', 'update', 'delete'],
      parents: ['create', 'read', 'update', 'delete'],
      menus: ['create', 'read', 'update', 'delete'],
      cameras: ['create', 'read', 'update', 'delete'],
      invites: ['create', 'read', 'update', 'delete'],
      admins: ['create', 'read', 'update', 'delete'],
    },
    manager: {
      kindergartens: ['read', 'update'],
      children: ['create', 'read', 'update'],
      parents: ['create', 'read', 'update'],
      menus: ['create', 'read', 'update'],
      cameras: ['read', 'update'],
      invites: ['create', 'read'],
      admins: ['read'],
    },
    kindergarten_admin: {
      kindergartens: ['read', 'update'],
      children: ['create', 'read', 'update', 'delete'],
      parents: ['create', 'read', 'update', 'delete'],
      menus: ['create', 'read', 'update', 'delete'],
      cameras: ['create', 'read', 'update', 'delete'],
      invites: ['create', 'read'],
      admins: ['read'],
    },
    viewer: {
      kindergartens: ['read'],
      children: ['read'],
      parents: ['read'],
      menus: ['read'],
      cameras: ['read'],
      invites: ['read'],
      admins: ['read'],
    },
  };

  const handleRoleChange = (role: string) => {
    setFormData(prev => ({
      ...prev,
      role,
      permissions: predefinedRoles[role as keyof typeof predefinedRoles] || prev.permissions
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Адміністратори системи
        </Typography>
        {hasPermission('admins', 'create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => handleOpenDialog()}
          >
            Додати адміністратора
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ім'я</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Роль</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата створення</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {admin.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        {admin.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(admin.role)}
                        color={getRoleColor(admin.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={admin.isActive ? 'Активний' : 'Неактивний'}
                        color={admin.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(admin.createdAt).toLocaleDateString('uk-UA')}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {hasPermission('admins', 'update') && (
                          <Tooltip title="Редагувати">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(admin)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('admins', 'delete') && canDeleteAdmin(admin) ? (
                          <Tooltip title="Видалити">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(admin.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        ) : hasPermission('admins', 'delete') && (
                          <Tooltip title={admin.id === currentAdmin?.id ? "Не можна видалити себе" : "Недостатньо прав для видалення"}>
                            <span>
                              <IconButton 
                                size="small" 
                                color="error"
                                disabled
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAdmin ? 'Редагувати адміністратора' : 'Додати адміністратора'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Ім'я адміністратора"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Пароль"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              margin="normal"
              required={!editingAdmin}
              helperText={editingAdmin ? 'Залиште порожнім, щоб не змінювати' : ''}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Роль</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                label="Роль"
              >
                <MenuItem value="super_admin">Головний адміністратор</MenuItem>
                <MenuItem value="manager">Менеджер</MenuItem>
                <MenuItem value="kindergarten_admin">Адміністратор садка</MenuItem>
                <MenuItem value="viewer">Переглядач</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Активний"
              sx={{ mt: 2 }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Права доступу
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Детальні права доступу</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                {Object.entries({
                  kindergartens: 'Дитячі садки',
                  children: 'Діти',
                  parents: 'Батьки',
                  menus: 'Меню',
                  cameras: 'Камери',
                  invites: 'Запрошення',
                  admins: 'Адміністратори'
                }).map(([resource, label]) => (
                  <Box key={resource} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {label}
                    </Typography>
                    {['create', 'read', 'update', 'delete'].map((action) => (
                      <FormControlLabel
                        key={action}
                        control={
                          <Switch
                            checked={formData.permissions[resource as keyof typeof formData.permissions]?.includes(action) || false}
                            onChange={(e) => handlePermissionChange(resource, action, e.target.checked)}
                            size="small"
                          />
                        }
                        label={action === 'create' ? 'Створювати' : 
                               action === 'read' ? 'Переглядати' :
                               action === 'update' ? 'Редагувати' : 'Видаляти'}
                        sx={{ display: 'block', mb: 0.5 }}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAdmin ? 'Зберегти' : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admins;
